import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { formatQuoteTitle } from 'Cube/utils/formatQuoteTitle'
import { ViewMode } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/useQuoteEditorHeader'

type UseQuoteTemplateEditorHeader = () => {
  title: string
  isArchived: boolean
  viewMode: {
    current: ViewMode
    onChange: (mode: string) => void
  }
  save: {
    visible: boolean
    disabled: boolean
    onClick: () => void
  }
}

export const useQuoteTemplateEditorHeader: UseQuoteTemplateEditorHeader =
  () => {
    const cubeContext = useCubeContext()
    const navigate = useNavigate()
    const location = useLocation()

    const quoteTemplateId = cubeContext.queries.rawData.data.common.id

    const onSelectPreview = useCallback(() => {
      if (quoteTemplateId) {
        navigate(`/quote-templates/${quoteTemplateId}/preview`)
      }
    }, [navigate, quoteTemplateId])

    const onSelectEdit = useCallback(() => {
      if (quoteTemplateId) {
        navigate(`/quote-templates/${quoteTemplateId}`)
      }
    }, [navigate, quoteTemplateId])

    const isPreviewMode = location.pathname.endsWith('/preview')

    return {
      title: formatQuoteTitle({
        domainTitle: cubeContext.queries.rawData.data.common.title,
        isArchived: cubeContext.queries.rawData.data.common.isArchived
      }),
      isArchived: cubeContext.queries.rawData.data.common.isArchived,
      viewMode: {
        current: isPreviewMode ? ViewMode.Preview : ViewMode.Edit,
        onChange: (mode: string) => {
          if (mode === ViewMode.Preview && isPreviewMode) return
          if (mode === ViewMode.Edit && !isPreviewMode) return

          if (mode === ViewMode.Preview) {
            onSelectPreview()
          } else {
            onSelectEdit()
          }
        }
      },
      save: {
        visible:
          cubeContext.queries.availableFeatures.quote.saveTemplate.available
            .visible,
        disabled:
          !cubeContext.queries.availableFeatures.quote.saveTemplate.available
            .enabled,
        onClick: () =>
          void cubeContext.mutators.external.out.save({ reload: true })
      }
    }
  }
